import { useQuery } from '@apollo/client';
import type * as React from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import UserProfile from '../../components/UserProfile';
import { useAuth } from '../../context/AuthContext';
import { GET_USER_QUERY } from '../../graphql/resolvers/userQueries/getUser.gql';
import { userSlice } from '../../store/user.store';
import PublicProfile from '../PublicProfile';

export default function UserProfileRoute(): React.ReactElement | null {
  const { username } = useParams<{ username: string }>();
  const { user: authUser, isSignedIn } = useAuth();
  const dispatch = useDispatch();
  const { UPDATE_USER_SUCCESS } = userSlice.actions;

  // Token-only signed-in state: AuthContext has a JWT-decoded user (id set,
  // username=''), but Redux/userData hasn't been hydrated yet. Self-heal by
  // firing GET_USER_QUERY (matches the UserDashboard hydration pattern) so
  // the Redux→AuthContext sync can populate the username and we re-render.
  const needsHydration = isSignedIn && !authUser?.username && !!authUser?.id;
  useQuery(GET_USER_QUERY, {
    variables: { userId: authUser?.id as string },
    skip: !needsHydration,
    onCompleted: (data) => {
      if (data?.getUser) {
        dispatch(UPDATE_USER_SUCCESS(data.getUser));
      }
    },
  });

  if (!username) return <Navigate to="/" replace />;
  if (!isSignedIn) return <Navigate to="/" replace />;

  // Authenticated but username not yet hydrated from userData (token-only
  // state — AuthContext returns a JWT-decoded user with username=''). Wait
  // for hydration rather than bouncing the user back to home.
  if (!authUser?.username) return null;

  const isOwnProfile = authUser.username === username;
  return isOwnProfile ? <UserProfile username={username} /> : <PublicProfile />;
}
